// Adaptación del "Resizable Navbar" de Aceternity UI al sistema.
//   - `motion/react` → `framer-motion` (ya instalada; misma librería).
//   - `@tabler/icons-react` → `lucide-react` (ya instalada).
//   - Paleta oscura de la demo → crema/vidrio/violeta del DS.
//   - `<a href>` de la demo → `<Link>` de react-router (una SPA no recarga).
//   - El par NavBody/MobileNav de la demo duplicaba los hijos en dos
//     árboles (uno `hidden lg:flex`, otro `lg:hidden`). Aquí `NavBody`
//     es UNA sola barra responsive: los anchos objetivo salen de un
//     media query en vez de duplicar el DOM — duplicarlo rompería los
//     `useId` de aria y las búsquedas por etiqueta de las pruebas.
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { cn } from "@/shared/lib/cn";

/** La sombra flotante de la referencia, tal cual (es neutra, no de marca). */
export const NAVBAR_FLOATING_SHADOW =
  "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), " +
  "0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), " +
  "0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset";

/** El muelle de la referencia: firme pero sin rebote. */
export const NAVBAR_SPRING = { type: "spring", stiffness: 200, damping: 50 } as const;

/** Píxeles de scroll a partir de los cuales la barra se encoge a píldora. */
const SCROLL_THRESHOLD = 100;

/**
 * `true` cuando el scroll de la ventana pasó el umbral: es el interruptor
 * barra-pegada → píldora-flotante. La demo lo sacaba de un ref al propio
 * nav; el scroll de la ventana es lo mismo con la barra en `top-0`.
 */
export function useNavbarVisible(): boolean {
  const { scrollY } = useScroll();
  const [visible, setVisible] = useState(false);
  useMotionValueEvent(scrollY, "change", (latest) => {
    setVisible(latest > SCROLL_THRESHOLD);
  });
  return visible;
}

/** Media query como estado. En jsdom el stub responde `false` (móvil). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
}

/*
 * El estado compartido viaja por contexto, no por `cloneElement` como en
 * la demo: clonar hijos exige que TODO hijo directo acepte `visible` y
 * rompe en cuanto alguien mete un fragmento en medio.
 */
const NavbarContext = createContext(false);

export interface NavbarProps {
  children: ReactNode;
  className?: string;
}

/**
 * El contenedor pegajoso. Él mide el scroll; los hijos (`NavBody`,
 * `MobileNavMenu`…) leen `useNavbarState()` y se animan solos.
 *
 * Es un `<header>` y no un `<div>` como en la demo: la barra del sitio
 * ES el banner de la página, y las pruebas (y los lectores de pantalla)
 * lo encuentran por ese rol.
 */
export function Navbar({ children, className }: NavbarProps) {
  const visible = useNavbarVisible();
  return (
    <NavbarContext.Provider value={visible}>
      <header className={cn("sticky inset-x-0 top-0 z-50 w-full", className)}>{children}</header>
    </NavbarContext.Provider>
  );
}

export function useNavbarState(): boolean {
  return useContext(NavbarContext);
}

export interface NavBodyProps {
  children: ReactNode;
  className?: string;
  /**
   * Ancho mínimo de la píldora en escritorio, para que el contenido no
   * se aplaste (la demo fijaba 800). Cada barra sabe cuánto necesita.
   */
  desktopMinWidth?: number;
}

/**
 * La barra que respira: a pantalla completa arriba del todo; al hacer
 * scroll se encoge a una píldora de vidrio centrada, con la sombra y el
 * muelle de la referencia. El color es del sistema: blanco translúcido
 * sobre el crema, borde `ds-border`.
 */
export function NavBody({ children, className, desktopMinWidth = 800 }: NavBodyProps) {
  const visible = useNavbarState();
  const desktop = useMediaQuery("(min-width: 1024px)");

  const style: CSSProperties | undefined =
    visible && desktop ? { minWidth: `${desktopMinWidth}px` } : undefined;

  return (
    <motion.div
      animate={{
        width: visible ? (desktop ? "64%" : "92%") : "100%",
        y: visible ? (desktop ? 16 : 10) : 0,
        borderRadius: visible ? "3rem" : "0rem",
        backdropFilter: visible ? "blur(10px)" : "blur(0px)",
        boxShadow: visible ? NAVBAR_FLOATING_SHADOW : "0 0 0 rgba(0,0,0,0)",
      }}
      transition={NAVBAR_SPRING}
      style={style}
      className={cn(
        "relative z-[60] mx-auto flex w-full max-w-[1440px] flex-row items-center justify-between",
        // El borde existe SIEMPRE (transparente en reposo): si apareciera
        // con la píldora, la barra saltaría 1px de alto a media animación.
        "border border-transparent bg-transparent",
        visible && "border-ds-border bg-white/80",
        className,
      )}
    >
      {children}
    </motion.div>
  );
}

export interface NavItemsProps {
  items: { name: string; link: string }[];
  className?: string;
  onItemClick?: () => void;
}

/**
 * Enlaces centrales con la píldora que persigue al cursor (`layoutId`).
 * La barra de la tienda no los usa —su navegación vive en el panel del
 * menú—, pero quedan listos para una landing con secciones.
 */
export function NavItems({ items, className, onItemClick }: NavItemsProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <motion.div
      onMouseLeave={() => setHovered(null)}
      className={cn(
        "absolute inset-0 hidden flex-1 flex-row items-center justify-center text-sm font-medium text-ds-ink-3 lg:flex",
        className,
      )}
    >
      {items.map((item, idx) => (
        <Link
          onMouseEnter={() => setHovered(idx)}
          onClick={onItemClick}
          className="relative px-4 py-2 transition-colors hover:text-ds-ink"
          key={`link-${idx}`}
          to={item.link}
        >
          {hovered === idx && (
            <motion.div
              layoutId="hovered"
              className="absolute inset-0 h-full w-full rounded-full bg-ds-mist"
            />
          )}
          <span className="relative z-20">{item.name}</span>
        </Link>
      ))}
    </motion.div>
  );
}

export interface MobileNavMenuProps {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
}

/** El desplegable móvil de la demo, en vidrio del sistema. */
export function MobileNavMenu({ children, className, isOpen }: MobileNavMenuProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            "absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-3xl border border-ds-border bg-white px-4 py-8",
            "shadow-[0_25px_60px_rgba(0,0,0,0.08)]",
            className,
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function MobileNavToggle({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) {
  return isOpen ? (
    <X className="h-5 w-5 text-ds-ink" onClick={onClick} />
  ) : (
    <Menu className="h-5 w-5 text-ds-ink" onClick={onClick} />
  );
}

export interface NavbarButtonProps {
  href?: string;
  as?: ElementType;
  children: ReactNode;
  className?: string;
  variant?: "primary" | "secondary";
  onClick?: () => void;
}

/**
 * El botón de la demo con la ropa del sistema: primario violeta,
 * secundario fantasma. Para CTAs dentro de una barra con `NavItems`;
 * el cromo real de la tienda sigue usando `Button` del DS.
 */
export function NavbarButton({
  href,
  as: Tag = "a",
  children,
  className,
  variant = "primary",
  ...props
}: NavbarButtonProps) {
  const baseStyles =
    "px-4 py-2 rounded-ds-pill text-sm font-bold relative cursor-pointer hover:-translate-y-0.5 transition duration-200 inline-block text-center";

  const variantStyles = {
    primary: "bg-ds-violet-pop text-white shadow-[0_8px_20px_rgba(92,79,214,0.35)]",
    secondary: "bg-transparent text-ds-ink-3 hover:text-ds-violet-pop",
  };

  return (
    <Tag href={href || undefined} className={cn(baseStyles, variantStyles[variant], className)} {...props}>
      {children}
    </Tag>
  );
}
